import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * 角色管理頁面
 * 參照 UI: UI-1-5 角色管理（RBAC 矩陣）
 * 功能：角色列表 + 建立 + 編輯（含頁面權限 + 操作權限矩陣）
 */
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { roleDisplayName, roleDisplayDescription } from '../../lib/roleDisplay';
import { useEditionStore } from '../../store/edition.store';
import { useAuthStore } from '../../store/auth.store';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import { adminVenueListOptions } from '../../hooks/adminVenueQueries';
import { apiClient } from '../../lib/api.ts';
import type {
  RoleDto,
  CreateRoleDto,
  UpdateRoleDto,
  PagePath,
  ActionKey,
  FeatureKey,
} from '@pokerroom/types';
import { ACTION_KEY_GROUPS, getConfigurablePages } from '@pokerroom/types';
import {
  actionPermissionLabelKey,
  ROLE_PAGE_GROUPS,
  ROLE_ACTION_GROUPS,
  orderPagePermissions,
  getRolePageDomains,
  setVisiblePermissions,
  permissionChanges,
  permissionRiskKey,
  initialPagePermissions,
  countPagePermissions,
} from './rolePermissionOrder';

/** API 回應包裝格式 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// ============================================================
// 從 SIDEBAR_DEFINITION 衍生所有頁面路徑（扁平列表）
// ============================================================
/** pagePath → labelKey 對照表（供 badge 顯示用） */
function getPageLabelKeyMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const entry of ROLE_PAGE_GROUPS) {
    if (entry.type === 'single' && entry.pagePath) {
      map[entry.pagePath] = entry.labelKey;
    } else if (entry.children) {
      for (const child of entry.children) {
        map[child.pagePath] = child.labelKey;
      }
    }
  }
  return map;
}

const PAGE_LABEL_KEY_MAP = getPageLabelKeyMap();

/** 所有 ActionKey 扁平列表 */
const ALL_ACTION_KEYS = ACTION_KEY_GROUPS.flatMap((g) => g.actions);

// ============================================================
// Indeterminate Checkbox
// ============================================================
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="accent-primary shrink-0"
    />
  );
}

// ============================================================
// 角色 Modal（建立 / 編輯共用）
// ============================================================
interface RoleModalProps {
  role?: RoleDto; // 若有 role 表示編輯模式
  onClose: () => void;
  onSubmit: (data: CreateRoleDto | UpdateRoleDto) => void;
  isLoading: boolean;
  errorMessage: string;
  onReload: () => void;
}

function RoleModal({ role, onClose, onSubmit, isLoading, errorMessage, onReload }: RoleModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const { venue } = useCurrentVenue();
  const enabledFeatures = useEditionStore((s) => s.enabledFeatures);
  const isSystemUser = useAuthStore((s) => s.user?.isSystemUser);
  const pageDomains = getRolePageDomains(
    isSystemUser ? undefined : (key) => enabledFeatures.includes(key),
  );
  const initialPages = initialPagePermissions(role?.pagePermissions);
  const initialActions = Object.fromEntries([
    ...ALL_ACTION_KEYS.map((key) => [key, false]),
    ...(role?.actionPermissions.map((p) => [p.actionKey, p.isAllowed]) ?? []),
  ]);
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [requireDeviceBinding, setRequireDeviceBinding] = useState(
    role?.requireDeviceBinding ?? true,
  );
  const [pagePerms, setPagePerms] = useState<Record<string, boolean>>(() => initialPages);
  const [actionPerms, setActionPerms] = useState<Record<string, boolean>>(() => initialActions);
  const [nameError, setNameError] = useState('');
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('all');
  const [review, setReview] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const submitting = useRef(false);
  useEffect(() => {
    if (!isLoading) submitting.current = false;
  }, [isLoading, errorMessage]);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLInputElement>('input:not(:disabled)')?.focus();
    return () => opener?.focus();
  }, []);
  const pageDiff = permissionChanges(initialPages, pagePerms);
  const actionDiff = permissionChanges(initialActions, actionPerms);
  const basicChanged =
    name !== (role?.name ?? '') ||
    description !== (role?.description ?? '') ||
    requireDeviceBinding !== (role?.requireDeviceBinding ?? true);
  const query = search.trim().toLocaleLowerCase();
  const matches = (...text: string[]) => text.join(' ').toLocaleLowerCase().includes(query);
  const visibleDomains = ROLE_ACTION_GROUPS.filter(
    (g) => domain === 'all' || domain === g.labelKey,
  ).map((g) => ({
    ...g,
    subgroups: g.subgroups
      .map((sub) => ({
        ...sub,
        actions: sub.actions.filter((a) =>
          matches(a, t(actionPermissionLabelKey(a)), t(g.labelKey), t(sub.labelKey)),
        ),
      }))
      .filter((sub) => sub.actions.length),
  }));
  const visibleActions = visibleDomains.flatMap((g) => g.subgroups.flatMap((sub) => sub.actions));
  const visiblePageDomains = pageDomains
    .filter((g) => domain === 'all' || domain === g.labelKey)
    .map((g) => ({
      ...g,
      pages: g.pages.filter((p) =>
        matches(p.pagePath, t(p.labelKey, { ns: 'common' }), t(g.labelKey)),
      ),
    }));
  const visiblePaths = visiblePageDomains.flatMap((g) => g.pages.map((p) => p.pagePath));
  const knownActions = new Set<string>(ALL_ACTION_KEYS);
  const knownPages = new Set<string>(getConfigurablePages().map((p) => p.pagePath));
  const unknown = [
    ...Object.keys(actionPerms)
      .filter((k) => !knownActions.has(k))
      .map((key) => ({ key, allowed: actionPerms[key] })),
    ...Object.keys(pagePerms)
      .filter((k) => !knownPages.has(k))
      .map((key) => ({ key, allowed: pagePerms[key] })),
  ];
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading || submitting.current || !venue) return;
    if (!name.trim()) {
      setNameError(t('roleNameRequired'));
      return;
    }
    if (role && !basicChanged && !pageDiff.length && !actionDiff.length) {
      onClose();
      return;
    }
    if (!review) {
      setReview(true);
      return;
    }
    submitting.current = true;
    onSubmit({
      name: role && name === role.name ? name : name.trim(),
      description: description.trim() || undefined,
      requireDeviceBinding,
      ...(role ? { expectedUpdatedAt: role.updatedAt } : {}),
      pagePermissions: Object.entries(pagePerms).map(([pagePath, isVisible]) => ({
        pagePath: pagePath as PagePath,
        isVisible,
      })),
      actionPermissions: Object.entries(actionPerms).map(([actionKey, isAllowed]) => ({
        actionKey: actionKey as ActionKey,
        isAllowed,
      })),
    });
  };
  const trapFocus = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!isLoading) onClose();
    }
    if (e.key !== 'Tab') return;
    const controls = Array.from(
      dialog.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled),input:not(:disabled),select:not(:disabled),summary',
      ) ?? [],
    );
    const first = controls[0],
      last = controls.at(-1);
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-modal-title"
        aria-busy={isLoading}
        onKeyDown={trapFocus}
        className="bg-base-100 rounded-2xl border border-base-300 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl p-4 sm:p-6"
      >
        <h2 id="role-modal-title" className="text-lg font-semibold mb-4">
          {role ? t('editRole') : t('createRole')}
        </h2>
        {errorMessage && (
          <div role="alert" className="mb-3 text-error">
            {errorMessage}{' '}
            <button type="button" disabled={isLoading} onClick={onReload} className="underline">
              {t('permissionReload')}
            </button>
          </div>
        )}
        <p className="text-sm mb-3">
          {t('permissionVenue', { venue: venue?.name ?? t('permissionVenueUnavailable') })}
        </p>
        {role?.systemKey === 'SUPER_ADMIN' && (
          <p className="text-sm text-warning mb-3">{t('permissionSuperAdmin')}</p>
        )}
        <form onSubmit={submit} onChange={() => setReview(false)}>
          <fieldset disabled={isLoading} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-sm">
                {t('roleNameLabel')}
                <input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameError('');
                  }}
                  disabled={!!role?.isSystemRole && role.systemKey !== 'SUPER_ADMIN'}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-base-200 border border-base-300"
                />
              </label>
              <label className="text-sm">
                {t('descriptionLabel')}
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-base-200 border border-base-300"
                />
              </label>
            </div>
            {nameError && (
              <p role="alert" className="text-error">
                {nameError}
              </p>
            )}
            <label className="flex gap-2 items-start text-sm">
              <input
                type="checkbox"
                checked={requireDeviceBinding}
                onChange={(e) => setRequireDeviceBinding(e.target.checked)}
                className="mt-1"
              />
              <span>
                {t('requireDeviceBinding')}
                <small className="block text-base-content/60">
                  {t('requireDeviceBindingHint')}
                </small>
              </span>
            </label>
            <p className="text-sm text-base-content/70">{t('permissionBoundary')}</p>
            <label className="block text-sm">
              {t('permissionSearch')}
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300"
              />
            </label>
            <div className="flex flex-wrap gap-2" aria-label={t('permissionDomains')}>
              {[{ labelKey: 'all' }, ...ROLE_ACTION_GROUPS].map((g) => (
                <button
                  key={g.labelKey}
                  type="button"
                  aria-pressed={domain === g.labelKey}
                  onClick={() => {
                    setDomain(g.labelKey);
                    setReview(false);
                  }}
                  className={`px-3 py-2 rounded-lg text-sm ${domain === g.labelKey ? 'bg-primary text-primary-content' : 'bg-base-200'}`}
                >
                  {t(g.labelKey === 'all' ? 'permissionAllDomains' : g.labelKey)}
                </button>
              ))}
            </div>
            <section aria-label={t('pageVisibility')} className="space-y-2">
              <h3 className="font-medium">{t('pageVisibility')}</h3>
              <div className="flex flex-wrap gap-3 text-sm">
                <button
                  type="button"
                  onClick={() => setPagePerms((p) => setVisiblePermissions(p, visiblePaths, true))}
                >
                  {t('permissionSelectVisible', { count: visiblePaths.length })}
                </button>
                <button
                  type="button"
                  onClick={() => setPagePerms((p) => setVisiblePermissions(p, visiblePaths, false))}
                >
                  {t('permissionClearVisible', { count: visiblePaths.length })}
                </button>
              </div>
              {visiblePageDomains.map(
                (g) =>
                  g.pages.length > 0 && (
                    <div
                      key={g.labelKey}
                      className="rounded-lg bg-base-200 border border-base-300 p-3"
                    >
                      <h4 className="text-sm font-medium mb-2">{t(g.labelKey)}</h4>
                      {g.pages.map((p) => (
                        <label key={p.pagePath} className="flex items-center gap-2 py-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={pagePerms[p.pagePath] ?? false}
                            onChange={() =>
                              setPagePerms((old) => ({ ...old, [p.pagePath]: !old[p.pagePath] }))
                            }
                          />
                          {t(p.labelKey, { ns: 'common' })}
                        </label>
                      ))}
                    </div>
                  ),
              )}
              {!visiblePaths.length && <p className="text-sm">{t('permissionEmpty')}</p>}
            </section>
            <section aria-label={t('actionPermissions')} className="space-y-2">
              <h3 className="font-medium">{t('actionPermissions')}</h3>
              <div className="flex flex-wrap gap-3 text-sm">
                <button
                  type="button"
                  onClick={() =>
                    setActionPerms((p) => setVisiblePermissions(p, visibleActions, true))
                  }
                >
                  {t('permissionSelectVisible', { count: visibleActions.length })}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setActionPerms((p) => setVisiblePermissions(p, visibleActions, false))
                  }
                >
                  {t('permissionClearVisible', { count: visibleActions.length })}
                </button>
              </div>
              {(domain === 'all' || domain === 'permissionDomain_member') && (
                <p className="text-sm text-base-content/70">{t('permissionFinanceReference')}</p>
              )}
              {visibleDomains.map((g) => {
                const actions = g.subgroups.flatMap((sub) => sub.actions),
                  count = actions.filter((k) => actionPerms[k]).length;
                return (
                  actions.length > 0 && (
                    <div
                      key={g.labelKey}
                      className="rounded-lg bg-base-200 border border-base-300 p-3"
                    >
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <IndeterminateCheckbox
                          checked={count === actions.length}
                          indeterminate={count > 0 && count < actions.length}
                          onChange={() =>
                            setActionPerms((p) =>
                              setVisiblePermissions(p, actions, count !== actions.length),
                            )
                          }
                        />
                        {t(g.labelKey)}
                        <span className="ml-auto">
                          {t('permissionSelectedCount', { selected: count, total: actions.length })}
                        </span>
                      </label>
                      <p className="text-xs text-base-content/60 mt-1">
                        {t('permissionGroupScope', { count: actions.length })}
                      </p>
                      {g.subgroups.map((sub) => (
                        <div key={sub.labelKey} className="mt-3">
                          <h4 className="text-xs text-base-content/60">{t(sub.labelKey)}</h4>
                          {sub.actions.map((key) => (
                            <label key={key} className="flex items-start gap-2 py-2 text-sm">
                              <input
                                type="checkbox"
                                checked={actionPerms[key] ?? false}
                                onChange={() => setActionPerms((p) => ({ ...p, [key]: !p[key] }))}
                                className="mt-1"
                              />
                              <span>
                                {t(actionPermissionLabelKey(key))}
                                <small className="block text-base-content/60 break-all">
                                  {key}
                                </small>
                                {permissionRiskKey(key) && (
                                  <small className="block text-warning">
                                    {t(permissionRiskKey(key)!)}
                                  </small>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  )
                );
              })}
              {!visibleActions.length && <p className="text-sm">{t('permissionEmpty')}</p>}
            </section>
            {unknown.length > 0 && (
              <details className="text-sm">
                <summary>{t('permissionUnknown', { count: unknown.length })}</summary>
                <p>{t('permissionUnknownHint')}</p>
                {unknown.map((p, i) => (
                  <p key={`${i}-${p.key}`} className="break-all">
                    {p.key}: {t(p.allowed ? 'permissionAllowed' : 'permissionNotAllowed')}
                  </p>
                ))}
              </details>
            )}
            {review && (
              <section
                aria-label={t('permissionReview')}
                className="p-3 rounded-lg border border-primary space-y-2 text-sm"
              >
                <h3 className="font-medium">{t('permissionReview')}</h3>
                <p>{t('permissionVenue', { venue: venue?.name })}</p>
                {basicChanged && <p>{t('permissionDetailsChanged')}</p>}
                {name !== (role?.name ?? '') && (
                  <p>
                    {t('roleNameLabel')}: {role?.name || '—'} → {name}
                  </p>
                )}
                {pageDiff.map((d) => (
                  <p key={d.key}>
                    {t(d.allowed ? 'permissionGrant' : 'permissionRevoke')}:{' '}
                    {PAGE_LABEL_KEY_MAP[d.key]
                      ? t(PAGE_LABEL_KEY_MAP[d.key]!, { ns: 'common' })
                      : d.key}
                  </p>
                ))}
                {actionDiff.map((d) => (
                  <p key={d.key}>
                    {t(d.allowed ? 'permissionGrant' : 'permissionRevoke')}:{' '}
                    {t(actionPermissionLabelKey(d.key))}
                  </p>
                ))}
              </section>
            )}
            <div className="flex gap-2 sticky bottom-0 bg-base-100 py-3">
              <button type="button" onClick={onClose} className="flex-1 p-2 rounded-lg bg-base-300">
                {t('cancelBtn')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !venue}
                className="flex-1 p-2 rounded-lg bg-primary text-primary-content disabled:opacity-50"
              >
                {isLoading
                  ? t('saving')
                  : review
                    ? t('permissionConfirmSave')
                    : role && !basicChanged && !pageDiff.length && !actionDiff.length
                      ? t('saveBtn')
                      : t('permissionReview')}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Main Page
// ============================================================

type ModalState = { type: 'none' } | { type: 'create' } | { type: 'edit'; role: RoleDto };

function AdminRolesPageContent({ revision }: { revision: number }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const enabledFeatures = useEditionStore((state) => state.enabledFeatures);
  const hasFeature = (key: FeatureKey) => enabledFeatures.includes(key);
  const isSystemUser = useAuthStore((state) => state.user?.isSystemUser);
  const featureFilter = isSystemUser ? undefined : hasFeature;
  const configurablePaths = new Set<string>(
    getConfigurablePages(featureFilter).map((page) => page.pagePath),
  );

  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (msgTimer.current) clearTimeout(msgTimer.current);
    },
    [],
  );

  const showMessage = (msg: string, isError = false) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    if (isError) {
      setErrorMessage(msg);
      msgTimer.current = setTimeout(() => setErrorMessage(''), 4000);
    } else {
      setSuccessMessage(msg);
      msgTimer.current = setTimeout(() => setSuccessMessage(''), 4000);
    }
  };

  // 查詢角色清單
  const {
    data: rolesData,
    isLoading,
    error: rolesError,
    refetch: refetchRoles,
  } = useQuery(adminVenueListOptions<RoleDto>('roles', revision));

  const roles = rolesData ?? [];
  // GET /roles is SUPER_ADMIN-only, and that identity bypasses action checks.
  // An explicit action list may be empty; a successful scoped role read is the UI capability signal.
  const canCreate = rolesData !== undefined && !rolesError && !isLoading;
  const canUpdate = canCreate;
  const mutationError = (err: unknown) => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return t(
      status === 409
        ? 'permissionConflict'
        : status === 403 || status === 401
          ? 'permissionDenied'
          : 'permissionRetry',
    );
  };
  const closeModal = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      setModal({ type: 'none' });
      setErrorMessage('');
    }
  };
  const reloadRoles = () => {
    queryClient.invalidateQueries({ queryKey: ['roles'] });
    closeModal();
  };

  // 建立角色
  const createMutation = useMutation({
    mutationFn: (data: CreateRoleDto) => apiClient.post<ApiResponse<RoleDto>>('/roles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setModal({ type: 'none' });
      showMessage(t('roleCreated'));
    },
    onError: (err: unknown) => {
      setErrorMessage(mutationError(err));
    },
  });

  // 更新角色
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleDto }) =>
      apiClient.patch<ApiResponse<RoleDto>>(`/roles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setModal({ type: 'none' });
      showMessage(t('roleUpdated'));
    },
    onError: (err: unknown) => {
      setErrorMessage(mutationError(err));
    },
  });

  return (
    <AdminPage>
      <div className="w-full min-w-0">
        {/* Header */}
        <AdminPageHeader
          title={t('roleManagement')}
          actions={
            <>
              <Link to="/admin/roles/permission-matrix" className="text-primary underline">
                {t('permissionMatrix')}
              </Link>
              <button
                disabled={!canCreate}
                onClick={() => {
                  setErrorMessage('');
                  setModal({ type: 'create' });
                }}
                className="px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-semibold hover:opacity-90 transition"
              >
                + {t('createRole')}
              </button>
            </>
          }
        />

        {/* Toast messages */}
        {successMessage && (
          <div
            role="status"
            className="mb-4 px-4 py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm"
          >
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div
            role="alert"
            className="mb-4 px-4 py-3 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 text-sm"
          >
            {errorMessage}
          </div>
        )}

        {rolesError && (
          <div role="alert" className="mb-3 text-error">
            {t('permissionLoadFailed')}{' '}
            <button type="button" onClick={() => refetchRoles()}>
              {t('permissionRetryButton')}
            </button>
          </div>
        )}
        {/* Roles Grid */}
        {isLoading ? (
          <div className="p-12 text-center text-base-content/40 text-sm">{t('loading')}</div>
        ) : roles.length === 0 ? (
          <div className="p-12 text-center text-base-content/40 text-sm bg-base-200 rounded-2xl border border-base-300">
            {t('noRoles')}
          </div>
        ) : (
          <div className="grid gap-4">
            {roles.map((role) => {
              const { visible: visiblePages, total: totalPages } = countPagePermissions(
                role.pagePermissions,
                featureFilter,
              );
              const allowedActions = role.actionPermissions.filter(
                (a) => a.isAllowed && ALL_ACTION_KEYS.includes(a.actionKey as ActionKey),
              ).length;
              const totalActions = ALL_ACTION_KEYS.length;

              return (
                <div
                  key={role.id}
                  className="bg-base-200 rounded-2xl border border-base-300 p-5 hover:border-primary/40 transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-base font-semibold text-base-content">
                          {roleDisplayName(role, t)}
                        </h2>
                        {role.isSystemRole && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-base-content/10 text-base-content/50 border border-base-content/20">
                            {t('systemBuiltin')}
                          </span>
                        )}
                      </div>
                      {role.systemKey === 'SUPER_ADMIN' && (
                        <p className="text-xs text-warning">{t('permissionSuperAdmin')}</p>
                      )}
                      {role.description && (
                        <p className="text-sm text-base-content/50 mb-3">
                          {roleDisplayDescription(role, t)}
                        </p>
                      )}
                      {/* 權限摘要 */}
                      <div className="flex gap-4">
                        <div
                          className="relative text-xs text-base-content/50"
                          title={t('pageCatalogScope')}
                        >
                          {t('pageVisibilitySummary')}
                          <span className="sr-only">{t('pageCatalogScope')}</span>
                          <span className="text-base-content font-medium">{visiblePages}</span>
                          <span className="text-base-content/30">/{totalPages}</span>
                        </div>
                        <div className="text-xs text-base-content/50">
                          {t('actionPermissionsSummary')}
                          <span className="text-base-content font-medium">{allowedActions}</span>
                          <span className="text-base-content/30">/{totalActions}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-2 text-xs">
                        {ROLE_ACTION_GROUPS.map((group) => (
                          <span key={group.labelKey}>
                            {t(group.labelKey)}:{' '}
                            {
                              role.actionPermissions.filter(
                                (a) =>
                                  a.isAllowed && group.actions.includes(a.actionKey as ActionKey),
                              ).length
                            }
                            /{group.actions.length}
                          </span>
                        ))}
                      </div>
                      {/* 已授權頁面 badges */}
                      {visiblePages > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {orderPagePermissions(role.pagePermissions)
                            .filter((p) => p.isVisible && configurablePaths.has(p.pagePath))
                            .slice(0, 6)
                            .map((p) => (
                              <span
                                key={p.pagePath}
                                className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20"
                              >
                                {PAGE_LABEL_KEY_MAP[p.pagePath]
                                  ? t(PAGE_LABEL_KEY_MAP[p.pagePath]!, { ns: 'common' })
                                  : p.pagePath}
                              </span>
                            ))}
                          {visiblePages > 6 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-base-300 text-base-content/50">
                              {t('plusMore', { count: visiblePages - 6 })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-4 shrink-0">
                      <button
                        disabled={!canUpdate}
                        onClick={() => {
                          setErrorMessage('');
                          setModal({ type: 'edit', role });
                        }}
                        className="px-3 py-1.5 rounded-lg bg-base-300 text-base-content/70 hover:bg-primary/15 hover:text-primary transition text-xs"
                      >
                        {t('editBtn')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && (
          <p className="text-xs text-base-content/40 mt-2">
            {t('totalRoles', { count: roles.length })}
          </p>
        )}
      </div>

      {/* Modals */}
      {modal.type === 'create' && (
        <RoleModal
          onClose={closeModal}
          errorMessage={errorMessage}
          onReload={reloadRoles}
          onSubmit={(data) => createMutation.mutate(data as CreateRoleDto)}
          isLoading={createMutation.isPending}
        />
      )}
      {modal.type === 'edit' && (
        <RoleModal
          role={modal.role}
          onClose={closeModal}
          errorMessage={errorMessage}
          onReload={reloadRoles}
          onSubmit={(data) => updateMutation.mutate({ id: modal.role.id, data })}
          isLoading={updateMutation.isPending}
        />
      )}
    </AdminPage>
  );
}

function AdminRolesPage() {
  const revision = useAuthStore((state) => state.sessionRevision);
  return <AdminRolesPageContent key={revision} revision={revision} />;
}

export default AdminRolesPage;
